"use client";

import { useEffect, useState, useSyncExternalStore } from "react";
import { ENTRY_SUGGESTED_PROMPTS } from "./entryOptions";

type AnimationPhase = "typing" | "holding" | "deleting";
const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

function subscribeReducedMotion(callback: () => void) {
  const media = window.matchMedia(REDUCED_MOTION_QUERY);
  media.addEventListener("change", callback);
  return () => media.removeEventListener("change", callback);
}

function getReducedMotion() {
  return window.matchMedia(REDUCED_MOTION_QUERY).matches;
}

function getServerReducedMotion() {
  return false;
}

/** A placeholder animation only: none of these words enter the customer's draft. */
export function useAnimatedEntryPrompt(active: boolean) {
  const reduceMotion = useSyncExternalStore(
    subscribeReducedMotion,
    getReducedMotion,
    getServerReducedMotion
  );
  const [index, setIndex] = useState(0);
  const [visibleCharacters, setVisibleCharacters] = useState(0);
  const [phase, setPhase] = useState<AnimationPhase>("typing");
  const suggestion = ENTRY_SUGGESTED_PROMPTS[index];

  useEffect(() => {
    if (!active || reduceMotion) return;

    const delay =
      phase === "holding"
        ? 2000
        : phase === "deleting"
          ? 34
          : visibleCharacters === 0
            ? 500
            : 64;
    const timer = window.setTimeout(() => {
      if (phase === "typing") {
        if (visibleCharacters >= suggestion.message.length) {
          setPhase("holding");
        } else {
          setVisibleCharacters(count => count + 1);
        }
      } else if (phase === "holding") {
        setPhase("deleting");
      } else if (visibleCharacters > 0) {
        setVisibleCharacters(count => count - 1);
      } else {
        setIndex(current => (current + 1) % ENTRY_SUGGESTED_PROMPTS.length);
        setPhase("typing");
      }
    }, delay);

    return () => window.clearTimeout(timer);
  }, [
    active,
    phase,
    reduceMotion,
    suggestion.message.length,
    visibleCharacters,
  ]);

  return {
    suggestion,
    visibleText: reduceMotion
      ? suggestion.message
      : suggestion.message.slice(0, visibleCharacters),
  };
}
